import { deleteHttp } from "@common/api/apiShared.ts";
import { createElement, useState } from "react";
import { useTranslation } from "@localization/useTranslation.ts";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import { useMasterDataBreadCrumbProvider } from "../../useMasterDataBreadCrumbProvider.ts";
import { Trans } from "react-i18next";
import { LegalEntityTypeViewModel } from "./LegalEntityType.types.ts";
import { useDotLegalQuery, useDotLegalMutation, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";

export default function useLegalEntityTypesHooks() {
    const url = "/legal-entity-types";
    const { isLoading, data: legalEntityTypes, refetch } = useDotLegalQuery<Array<LegalEntityTypeViewModel>>(url);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const { translateString } = useTranslation();
    const [legalEntityTypeForDeletion, setLegalEntityTypeForDeletion] = useState<LegalEntityTypeViewModel | undefined>(undefined);
    const [legalEntityTypeForUpdate, setLegalEntityTypeForUpdate] = useState<LegalEntityTypeViewModel | undefined>();
    const snackBar = useDotLegalSnackbar();
    const queryClient = useDotLegalQueryClient();

    const { permissions } = useUserContext();
    const masterDataBreadCrumbProvider = useMasterDataBreadCrumbProvider();
    const breadCrumbs = masterDataBreadCrumbProvider.getBreadCrumbs(translateString("legalEntityTypes"));

    const deleteLegalEntityType = async (id: string) => {
        return await deleteHttp(`/legal-entity-types/${id}`);
    };

    const deleteMutation = useDotLegalMutation(deleteLegalEntityType, {
        onSuccess: async (result) => {
            if (result.success()) {
                await refetchLegalEntityTypes();
                snackBar.show(createElement(Trans, { i18nKey: "itemDeletedSuccess", values: { name: legalEntityTypeForDeletion?.name } }));
            } else {
                snackBar.show(createElement(Trans, { i18nKey: "cantDeleteLegalEntityType", values: { name: legalEntityTypeForDeletion?.name } }), "warning");
            }
            setLegalEntityTypeForDeletion(undefined);
        },
    });

    const refetchLegalEntityTypes = async () => {
        await Promise.all([refetch(), queryClient.refetchQueries("selectableLegalEntityTypes")]);
    };

    const onCreateClick = () => {
        setShowCreateDialog(true);
    };

    const onSaved = async () => {
        await refetchLegalEntityTypes();
        onDialogClose();
    };

    const onDialogClose = () => {
        setLegalEntityTypeForUpdate(undefined);
        setShowCreateDialog(false);
    };

    const onDelete = () => {
        deleteMutation.mutate(legalEntityTypeForDeletion!.id);
    };

    // The standard types are the same for every customer, so only a type the customer created can be changed.
    const getMenuItems = (legalEntityType: LegalEntityTypeViewModel) => {
        let menuItems = [];
        if (permissions.canManageMasterData && legalEntityType.isCustom) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setLegalEntityTypeForUpdate(legalEntityType);
                },
            });

            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => {
                    setLegalEntityTypeForDeletion(legalEntityType);
                },
            });
        }

        return menuItems;
    };

    return {
        isLoading,
        legalEntityTypes,
        breadCrumbs,
        getMenuItems,
        canManageMasterData: permissions.canManageMasterData,
        showCreateDialog,
        onSaved,
        legalEntityTypeForDeletion,
        legalEntityTypeForUpdate,
        setLegalEntityTypeForDeletion,
        onDelete,
        onCreateClick,
        onDialogClose,
        isDeleting: deleteMutation.isLoading,
    };
}
